import React, { useCallback, useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useParams } from "react-router-dom";
import moment from "moment";
import { InfoOutlined } from "@mui/icons-material";
import {
  UpfrontPaymentAccordionContainer,
  UpfrontPaymentCost,
  UpfrontPaymentFieldRowTitle,
  UpfrontPaymentInfoBillContent,
  UpfrontPaymentInfoRow,
  UpfrontPaymentMainBanner,
  UpfrontPaymentMainBannerBottomRow,
  UpfrontPaymentMainBannerTopRow,
  UpfrontPaymentMainText,
  UpfrontPaymentPlanAccordionSummary,
  UpfrontPaymentPlanAccordionTitle,
  UpfrontPaymentPlanTitle,
  UpfrontPaymentYouHaveABill,
  UpfrontPaymentFieldRow,
  UpfrontPaymentSolidLine,
  UpfrontPaymentButtonRow,
} from "./UpfrontPayment.styles";

import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import {
  GetUpfrontPaymentPlan,
  GetSavedCardsUpfront,
  GetCurrentPaymentMethodForPaymentPlanUpfront,
  UpfrontPaymentPlan,
  Bill,
  getCentsToDollars,
} from "./Services/UpfrontPaymentService";
import { SavedCardType } from "../BabyPeek/Services/BabyPeekService";
import { getUserAuth } from "../../recoil/selectors";
import { failedPaymentBillPkState } from "../../recoil/atoms";
import { TrackAnalyticsEvent } from "../../services/Analytics";

import { GetTypographyContent } from "../shared/Typography/Typography";
import { UpfrontPaymentPlanSteps } from "./UpfrontPaymentPlanSteps";

import { BabyPeekIntroFAQAccordionWrapper } from "./PaymentPlanNumPayments.styles";
import UpfrontPaymentPhoneLink from "./UpfrontPaymentPhoneLink";
import { EditPaymentMethodModal } from "./EditPaymentMethodModal";
import { CardIconImage } from "../PaymentMethod/PaymentMethod.styles";
import { getCardIcon } from "../BabyPeek/BabyPeekStripe";
import { Button } from "../shared/Button/Button";
import { ErrorPopup } from "../shared/ErrorPopup/ErrorPopup";
import {
  savedPaymentMethodsErrorMessage,
  currentPaymentMethodErrorMessage,
} from "../../utils/helpers";
import { PaymentPlanPayoffModal } from "./PaymentPlanPayoffModal";
import { InstallmentPaymentModal } from "./InstallmentPaymentModal";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";

export const UpfrontPaymentPlanAccordion: React.FC<{
  upfrontBill: Bill;
  currentPaymentMethodForPaymentPlan?: SavedCardType;
  setSavedPayments?: React.Dispatch<
    React.SetStateAction<SavedCardType[] | undefined>
  >;
  setCurrentPaymentMethodForPaymentPlan?: React.Dispatch<
    React.SetStateAction<SavedCardType | undefined>
  >;
  setUpfrontBill?: React.Dispatch<React.SetStateAction<Bill | undefined>>;
}> = ({
  upfrontBill,
  currentPaymentMethodForPaymentPlan,
  setSavedPayments,
  setCurrentPaymentMethodForPaymentPlan,
  setUpfrontBill,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();

  const [paymentPlan, setPaymentPlan] = useState<UpfrontPaymentPlan[]>();
  const [numberPayments, setNumberPayments] = useState<"6">();
  const [showEditPaymentMethodModal, setShowEditPaymentMethodModal] =
    useState(false);
  const [showPayoffModal, setShowPayoffModal] = useState(false);
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] =
    useState<UpfrontPaymentPlan | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoadingPaymentPlan, setIsLoadingPaymentPlan] = useState(false);
  const auth = useRecoilValue(getUserAuth);
  const failedPaymentBillPk = useRecoilValue(failedPaymentBillPkState);

  const fetchPaymentPlan = useCallback(async () => {
    if (upfrontBill.is_payment_plan_enabled) {
      setIsLoadingPaymentPlan(true);
      try {
        const paymentPlanResponse = await GetUpfrontPaymentPlan(
          billingPK,
          adminID,
        );
        const paymentPlanJson = await paymentPlanResponse.json();
        setPaymentPlan(paymentPlanJson);
        if (paymentPlanJson) {
          setNumberPayments(paymentPlanJson.length.toString());
        }
      } finally {
        setIsLoadingPaymentPlan(false);
      }
    } else {
      // Clear payment plan data when bill is no longer a payment plan
      setPaymentPlan(undefined);
      setNumberPayments(undefined);
    }
  }, [upfrontBill.is_payment_plan_enabled, billingPK, adminID]);

  useEffect(() => {
    fetchPaymentPlan();
  }, [fetchPaymentPlan, upfrontBill.portal_bill_status]);

  const nextPayment = paymentPlan?.find((payment) => payment.status !== "paid");

  const remainingPayments = paymentPlan?.filter(
    (payment) => payment.status !== "paid",
  );

  const paidPayment = paymentPlan?.find((payment) => payment.status === "paid");

  const handlePaymentMethodUpdateSuccess = async (paymentMethodInfo: {
    card_brand: string;
    last_four_digits: string;
  }) => {
    if (setCurrentPaymentMethodForPaymentPlan) {
      try {
        const currentPaymentMethodResponse =
          await GetCurrentPaymentMethodForPaymentPlanUpfront(billingPK);
        if (currentPaymentMethodResponse.ok) {
          const currentPaymentMethodJson =
            await currentPaymentMethodResponse.json();
          const updatedCurrentPaymentMethod =
            currentPaymentMethodJson.payment_methods?.[0];
          setCurrentPaymentMethodForPaymentPlan(updatedCurrentPaymentMethod);
        } else {
          setErrorMessage(currentPaymentMethodErrorMessage);
        }
      } catch (error) {
        setErrorMessage(currentPaymentMethodErrorMessage);
      }
    }
    if (auth?.access_token) {
      if (setSavedPayments) {
        try {
          const savedCardsResponse = await GetSavedCardsUpfront(
            billingPK,
            auth.access_token,
          );
          if (savedCardsResponse.ok) {
            const savedCardsJson = await savedCardsResponse.json();
            setSavedPayments(savedCardsJson.payment_methods);
          } else {
            setErrorMessage(savedPaymentMethodsErrorMessage);
          }
        } catch (error) {
          setErrorMessage(savedPaymentMethodsErrorMessage);
        }
      }
    }
  };

  const handlePayNowClick = (installment: UpfrontPaymentPlan) => {
    setSelectedInstallment(installment);
    setShowInstallmentModal(true);
  };

  const handleInstallmentPaymentSuccess = (updatedBill: Bill) => {
    // Don't close the modal here - let the user close it after viewing the receipt
    if (setUpfrontBill) {
      setUpfrontBill(updatedBill);
    }
    fetchPaymentPlan();
  };

  const handleInstallmentModalClose = () => {
    setShowInstallmentModal(false);
    setSelectedInstallment(null);
  };

  return (
    <>
      {errorMessage && (
        <ErrorPopup
          message={errorMessage}
          hideMessage={() => setErrorMessage("")}
        />
      )}
      <UpfrontPaymentAccordionContainer deviceType={deviceType} padding={false}>
        <Accordion defaultExpanded={failedPaymentBillPk === upfrontBill.pk}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <UpfrontPaymentPlanAccordionSummary>
              <UpfrontPaymentFieldRowTitle>
                {t("upfrontPayment.paymentPlan.title")}
              </UpfrontPaymentFieldRowTitle>
              <UpfrontPaymentInfoBillContent>
                <UpfrontPaymentPlanTitle>
                  <UpfrontPaymentCost>
                    {getCentsToDollars(upfrontBill?.start_of_payment_amount)}
                  </UpfrontPaymentCost>
                  <UpfrontPaymentMainText>
                    {t("upfrontPayment.paymentPlan.splitMonthly")}
                  </UpfrontPaymentMainText>
                </UpfrontPaymentPlanTitle>
              </UpfrontPaymentInfoBillContent>
            </UpfrontPaymentPlanAccordionSummary>
          </AccordionSummary>
          <AccordionDetails>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.paymentPlan.nextPaymentDate")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content:
                    nextPayment &&
                    moment(new Date(nextPayment.epoch * 1000)).format(
                      "MMMM Do, YYYY",
                    ),
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.paymentPlan.nextPayment")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: getCentsToDollars(nextPayment?.amount),
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.paymentPlan.youHavePaid")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: getCentsToDollars(upfrontBill?.paid_amount),
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.editPaymentMethod.paymentMethod")}
                </UpfrontPaymentFieldRowTitle>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  {currentPaymentMethodForPaymentPlan && (
                    <>
                      <CardIconImage
                        src={getCardIcon(
                          currentPaymentMethodForPaymentPlan.card_brand,
                        )}
                        alt={`${currentPaymentMethodForPaymentPlan.card_brand} card`}
                      />
                      {GetTypographyContent({
                        content: `•••• ${currentPaymentMethodForPaymentPlan.last_four_digits}`,
                        size: "medium",
                        textColor: "black",
                      })}
                    </>
                  )}
                  {GetTypographyContent({
                    content: t("upfrontPayment.actions.edit"),
                    size: "large",
                    textColor: "redMedium",
                    onClick: () => {
                      TrackAnalyticsEvent(
                        "interaction",
                        undefined,
                        upfrontBill.req_identifier,
                        {
                          interaction_type: "button_click",
                          button_text: "Edit",
                          element_name: "edit_payment_method_inline_link",
                          page_name: "/client/upfront-payment",
                        },
                      );
                      setShowEditPaymentMethodModal(true);
                    },
                    className: "edit-button",
                    isCaps: false,
                  })}
                </div>
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.paymentPlan.remainingBalance")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: getCentsToDollars(upfrontBill?.balance),
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.paymentPlan.remainingPayments")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: remainingPayments?.length,
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            <UpfrontPaymentSolidLine />
            <UpfrontPaymentPlanAccordionTitle>
              <div>{t("upfrontPayment.paymentPlan.paymentSchedule")}</div>{" "}
              <BabyPeekIntroFAQAccordionWrapper>
                {t("upfrontPayment.paymentPlan.paymentCount", {
                  count: Number(numberPayments || 6),
                })}
              </BabyPeekIntroFAQAccordionWrapper>
            </UpfrontPaymentPlanAccordionTitle>
            {isLoadingPaymentPlan ? (
              <SpinnerLoading />
            ) : (
              paymentPlan && (
                <UpfrontPaymentPlanSteps
                  steps={paymentPlan}
                  onPayNowClick={handlePayNowClick}
                />
              )
            )}
            <UpfrontPaymentSolidLine />
            <UpfrontPaymentInfoBillContent style={{ marginTop: "10px" }}>
              <UpfrontPaymentMainText>
                {t("upfrontPayment.paymentPlan.noInterest")}
              </UpfrontPaymentMainText>
              <UpfrontPaymentMainText>
                {t("upfrontPayment.paymentPlan.totalAmount", {
                  amount: getCentsToDollars(
                    upfrontBill?.start_of_payment_amount,
                  ),
                })}
              </UpfrontPaymentMainText>
            </UpfrontPaymentInfoBillContent>
            <UpfrontPaymentInfoBillContent>
              <UpfrontPaymentYouHaveABill>
                <UpfrontPaymentMainBanner>
                  <UpfrontPaymentMainBannerTopRow>
                    <InfoOutlined /> {t("upfrontPayment.paymentPlan.needHelp")}
                  </UpfrontPaymentMainBannerTopRow>
                  <UpfrontPaymentMainBannerBottomRow>
                    {t("upfrontPayment.paymentPlan.helpPrefix")}

                    <>
                      <UpfrontPaymentPhoneLink />
                      {t("upfrontPayment.paymentPlan.helpSuffix")}
                    </>
                  </UpfrontPaymentMainBannerBottomRow>
                </UpfrontPaymentMainBanner>
              </UpfrontPaymentYouHaveABill>
            </UpfrontPaymentInfoBillContent>
          </AccordionDetails>
        </Accordion>
      </UpfrontPaymentAccordionContainer>

      {/* Only show pay off buttons if bill is not paid and has remaining balance */}
      {upfrontBill.portal_bill_status === "in_progress" &&
        upfrontBill.balance > 0 && (
          <div style={{ marginBottom: "24px" }}>
            <UpfrontPaymentButtonRow>
              <Button
                label={t("upfrontPayment.paymentPlan.payOffAmount", {
                  amount: getCentsToDollars(upfrontBill?.balance),
                })}
                onClick={() => {
                  TrackAnalyticsEvent(
                    "PayOffPaymentPlanButtonClicked",
                    undefined,
                    upfrontBill.req_identifier,
                    {
                      interaction_type: "button_click",
                      button_text: `Pay Off ${getCentsToDollars(upfrontBill?.balance)}`,
                      element_name: "pay_off_payment_plan_button",
                      page_name: "/client/upfront-payment",
                    },
                  );

                  // Open the dedicated payoff modal
                  setShowPayoffModal(true);
                }}
                size="medium"
                backgroundColor="greenDark"
              />
            </UpfrontPaymentButtonRow>
            <UpfrontPaymentButtonRow bottomRow>
              <Button
                label={t("upfrontPayment.paymentPlan.editCardOnFile")}
                onClick={() => {
                  TrackAnalyticsEvent(
                    "interaction",
                    undefined,
                    upfrontBill.req_identifier,
                    {
                      interaction_type: "button_click",
                      button_text: "Edit card on file",
                      element_name: "edit_payment_method_button",
                      page_name: "/client/upfront-payment",
                    },
                  );
                  setShowEditPaymentMethodModal(true);
                }}
                size="medium"
                backgroundColor="greenDark"
                reverse
              />
            </UpfrontPaymentButtonRow>
          </div>
        )}

      <EditPaymentMethodModal
        open={showEditPaymentMethodModal}
        onClose={() => setShowEditPaymentMethodModal(false)}
        onSuccess={handlePaymentMethodUpdateSuccess}
        reqIdentifier={upfrontBill.req_identifier}
      />

      <PaymentPlanPayoffModal
        open={showPayoffModal}
        onClose={() => setShowPayoffModal(false)}
        onSuccess={(updatedBill) => {
          if (setUpfrontBill) {
            setUpfrontBill(updatedBill);
          }
        }}
        onError={fetchPaymentPlan}
        upfrontBill={upfrontBill}
        currentPaymentMethod={currentPaymentMethodForPaymentPlan}
        onPaymentMethodUpdate={(paymentMethodInfo) => {
          // Update the current payment method display
          if (setCurrentPaymentMethodForPaymentPlan) {
            setCurrentPaymentMethodForPaymentPlan((prev) =>
              prev
                ? {
                    ...prev,
                    payment_method_id: paymentMethodInfo.payment_method_id,
                    card_brand: paymentMethodInfo.card_brand,
                    last_four_digits: paymentMethodInfo.last_four_digits,
                  }
                : undefined,
            );
          }
        }}
      />

      {selectedInstallment && (
        <InstallmentPaymentModal
          open={showInstallmentModal}
          onClose={handleInstallmentModalClose}
          onSuccess={handleInstallmentPaymentSuccess}
          onError={fetchPaymentPlan}
          upfrontBill={upfrontBill}
          installment={selectedInstallment}
          currentPaymentMethod={currentPaymentMethodForPaymentPlan}
          onPaymentMethodUpdate={(paymentMethodInfo) => {
            if (setCurrentPaymentMethodForPaymentPlan) {
              setCurrentPaymentMethodForPaymentPlan((prev) =>
                prev
                  ? {
                      ...prev,
                      payment_method_id: paymentMethodInfo.payment_method_id,
                      card_brand: paymentMethodInfo.card_brand,
                      last_four_digits: paymentMethodInfo.last_four_digits,
                    }
                  : undefined,
              );
            }
          }}
        />
      )}
    </>
  );
};
